import type { Spectrogram } from '../../core/media/spectrogram';

// Colour for a spectrogram, taken from the theme rather than from a stock
// colormap.
//
// Viridis, magma and the rest are built to be perceptually uniform against a
// white page in a paper. Dropped into this app they read as a screenshot from
// another program — none of those blues and purples exists anywhere else in
// Syrinx — and viridis in particular puts its darkest end at a blue that fights
// the strip's mossy ground.
//
// So the ramp is built from the palette's own hues, in luminance order, and it
// starts AT the strip's background: silence is the panel, and everything above
// it climbs bark → moss → lichen → brass → paper. Both themes define all five,
// both keep them in the same luminance order, and the strip's ground is dark in
// both (--moss-deep is the darkest surface in hearth and a raised stone in
// moss), so one ramp survives the swap with nothing per-theme about it except
// the values it reads.

/**
 * Ramp stops: position 0..1, custom property, and a fallback hex.
 *
 * The three middle stops sit early on purpose. Spaced evenly, an ordinary bird
 * at half the clip's peak loudness lands in the moss green a shade off the
 * background and reads as barely there; pulled down, the same cell reaches
 * lichen and brass, and the picture carries from across the room. The dark end
 * is unmoved — silence is still exactly the panel.
 */
const STOPS: [number, string, string][] = [
  [0, '--moss-deep', '#3a4630'],
  [0.22, '--moss', '#5c6b45'],
  [0.45, '--lichen', '#8ea376'],
  [0.72, '--honey', '#d19a3c'],
  [1, '--on-dark', '#f4ece0'],
];

type Rgb = [number, number, number];

let lut: Uint8ClampedArray | null = null;
let lutTheme = '';

function hex(value: string, fallback: string): Rgb {
  const raw = (value.trim() || fallback).replace('#', '');
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const n = Number.parseInt(full.slice(0, 6), 16);
  if (full.length < 6 || Number.isNaN(n)) return hex(fallback, '#000000');
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function token(name: string, fallback: string): Rgb {
  return hex(
    getComputedStyle(document.documentElement).getPropertyValue(name),
    fallback,
  );
}

/**
 * 256 RGB triples, rebuilt when the theme changes.
 *
 * Interpolation is in plain sRGB. A perceptual space would space the greys more
 * evenly, but the stops are close enough together that the difference is
 * invisible at 90 strip-pixels tall, and it would mean a colour-space conversion
 * in the app for one gradient.
 */
function ramp(): Uint8ClampedArray {
  const theme = document.documentElement.dataset.theme ?? '';
  if (lut && theme === lutTheme) return lut;
  const stops = STOPS.map(([at, name, fallback]) => [at, token(name, fallback)] as const);
  const out = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i++) {
    const t = i / 255;
    let hi = 1;
    while (hi < stops.length - 1 && stops[hi][0] < t) hi++;
    const [aAt, a] = stops[hi - 1];
    const [bAt, b] = stops[hi];
    const k = bAt === aAt ? 0 : (t - aAt) / (bAt - aAt);
    out[i * 3] = a[0] + (b[0] - a[0]) * k;
    out[i * 3 + 1] = a[1] + (b[1] - a[1]) * k;
    out[i * 3 + 2] = a[2] + (b[2] - a[2]) * k;
  }
  lut = out;
  lutTheme = theme;
  return out;
}

/**
 * The spectrogram as an image, one pixel per cell.
 *
 * Deliberately NOT scaled to the strip here: the canvas can do that in one
 * drawImage with its own smoothing, and keeping the picture at its measured
 * resolution means a resize costs a rescale rather than another transform.
 */
export function spectrogramImage(spec: Spectrogram): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = spec.columns;
  canvas.height = spec.rows;
  const g = canvas.getContext('2d');
  if (!g) return canvas;
  const image = g.createImageData(spec.columns, spec.rows);
  const px = image.data;
  const colors = ramp();
  for (let i = 0; i < spec.data.length; i++) {
    const at = spec.data[i] * 3;
    px[i * 4] = colors[at];
    px[i * 4 + 1] = colors[at + 1];
    px[i * 4 + 2] = colors[at + 2];
    px[i * 4 + 3] = 255;
  }
  g.putImageData(image, 0, 0);
  return canvas;
}

/**
 * The same ramp as a CSS gradient, for the swatch on the You screen: silence at
 * the left, the loudest cell in the clip at the right.
 */
export function rampGradient(): string {
  const stops = STOPS.map(([at, name, fallback]) => {
    const [r, g, b] = token(name, fallback);
    return `rgb(${r} ${g} ${b}) ${Math.round(at * 100)}%`;
  });
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

/** A gridline every 2 kHz, which is how a field guide rules the axis. */
export const GRID_HZ = 2000;

export interface StripPaint {
  /** The coloured image, from spectrogramImage. */
  image: HTMLCanvasElement;
  /** Ceiling of the band, for the gridlines. */
  maxHz: number;
  /** Played fraction, 0..1. Zero draws no playhead. */
  progress?: number;
  /** Alpha for the part not played yet. */
  dimAlpha?: number;
  /** Fraction of the width the picture covers. 1 unless it is short of the axis. */
  span?: number;
}

/**
 * One spectrogram strip, drawn the same way wherever a strip is drawn: the app's
 * player and the curation tool's clip list both call this, so how loud reads as
 * how bright — and how far in you are reads as how lit — is decided once.
 *
 * The context is expected to be scaled to CSS pixels already, and `w`/`h` given
 * in those.
 */
export function paintSpectrogramStrip(
  g: CanvasRenderingContext2D,
  w: number,
  h: number,
  opts: StripPaint,
): void {
  const progress = opts.progress ?? 0;
  const spanW = w * Math.min(1, opts.span ?? 1);
  // Smoothed on the way up: a spectrogram is a continuous surface, and
  // nearest-neighbour would draw the transform's own grid rather than the
  // sound. The image is oversampled, so this is a gentle stretch at most.
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.globalAlpha = opts.dimAlpha ?? 1;
  g.drawImage(opts.image, 0, 0, spanW, h);
  if (progress > 0) {
    g.save();
    g.beginPath();
    g.rect(0, 0, progress * w, h);
    g.clip();
    g.globalAlpha = 1;
    g.drawImage(opts.image, 0, 0, spanW, h);
    g.restore();
  }
  g.globalAlpha = 1;

  g.strokeStyle = cssValue('--overlay-med', 'rgba(255,255,255,0.12)');
  g.lineWidth = 1;
  for (let hz = GRID_HZ; hz < opts.maxHz; hz += GRID_HZ) {
    const y = Math.round(h * (1 - hz / opts.maxHz)) + 0.5;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(w, y);
    g.stroke();
  }

  if (progress <= 0) return;
  g.strokeStyle = cssValue('--on-dark', '#f4ece0');
  g.beginPath();
  g.moveTo(progress * w, 0);
  g.lineTo(progress * w, h);
  g.stroke();
}

/** A custom property as written, for the values that are not colours to mix. */
function cssValue(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}
